import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { MadafricaLogo } from "../components/MadafricaLogo";
import { Mail, Lock, LogIn, Eye, Mic2 } from "lucide-react";

const loadGoogleScript = () => new Promise((resolve) => {
  if (window.google?.accounts?.id) return resolve();
  const existing = document.getElementById("gsi-script");
  if (existing) { existing.addEventListener("load", () => resolve()); return; }
  const s = document.createElement("script");
  s.id = "gsi-script";
  s.src = "https://accounts.google.com/gsi/client";
  s.async = true;
  s.defer = true;
  s.onload = () => resolve();
  document.head.appendChild(s);
});

export const LoginPage = () => {
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Si une page nous a redirigé ici (ex: /chat), on revient dessus après login.
  // Sécurité : on n'accepte que les chemins relatifs internes.
  const rawRedirect = params.get("redirect") || "/";
  const redirectTo = (rawRedirect && rawRedirect.startsWith("/") && !rawRedirect.startsWith("//")) ? rawRedirect : "/";

  // ─── Redirect si déjà connecté ───
  useEffect(() => {
    if (user) navigate(redirectTo, { replace: true });
  }, [user, redirectTo, navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [mode, setMode] = useState("visitor"); // "visitor" | "artist" — cosmétique + CTA
  const googleBtnRef = useRef(null);

  // ─── Branding ARNAK sur arnakclan.com ───
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);
  const [arnakLogo, setArnakLogo] = useState(null);
  const [arnakName, setArnakName] = useState("ARNAK");
  useEffect(() => {
    if (!isArnakDomain) return;
    api.get("/arnak/public")
      .then(({ data }) => {
        if (data?.settings?.logo_url) setArnakLogo(data.settings.logo_url);
        if (data?.settings?.name) setArnakName(data.settings.name);
      })
      .catch(() => {});
  }, [isArnakDomain]);

  useEffect(() => {
    api.get("/auth/google/config")
      .then(({ data }) => { if (data.enabled) setGoogleClientId(data.client_id); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) return;
    let cancelled = false;
    loadGoogleScript().then(() => {
      if (cancelled || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (resp) => {
          try {
            await googleLogin(resp.credential);
            toast.success("Bienvenue !");
            navigate(redirectTo);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Connexion Google échouée");
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "filled_black", size: "large", width: 360, shape: "pill", text: "continue_with",
      });
    });
    return () => { cancelled = true; };
  }, [googleClientId, googleLogin, navigate, redirectTo]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenue !");
      navigate(redirectTo);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Connexion échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="block mb-10 text-center">
          {isArnakDomain ? (
            <div className="inline-flex flex-col items-center gap-3" data-testid="login-arnak-logo">
              {arnakLogo ? (
                <img
                  src={assetUrl(arnakLogo)}
                  alt={arnakName}
                  className="h-28 w-auto object-contain drop-shadow-[0_4px_18px_rgba(250,204,21,0.45)]"
                />
              ) : (
                <div className="h-28 w-28 rounded-2xl bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 flex items-center justify-center shadow-2xl shadow-yellow-400/30">
                  <span className="font-black text-black text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
                    ARNAK
                  </span>
                </div>
              )}
              <p className="text-[10px] uppercase tracking-[0.3em] text-yellow-400/80 font-black">
                Rap Gasy · Hip-Hop · Madagascar
              </p>
            </div>
          ) : (
            <div className="inline-block"><MadafricaLogo size={96} withText showTopMadaBadge={false} /></div>
          )}
        </Link>

        <div className="border border-white/10 bg-white/[0.02] rounded-2xl p-8">
          {/* Segmented control : Visiteur / Artiste */}
          <div className="flex gap-1 p-1 bg-black/30 rounded-xl border border-white/10 mb-6" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => setMode("visitor")}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg transition-colors ${
                mode === "visitor" ? "bg-brand-yellow/20 text-brand-yellow" : "text-white/50 hover:text-white"
              }`}
              data-testid="login-mode-visitor"
            >
              <Eye size={13} /> Visiteur
            </button>
            <button
              type="button"
              onClick={() => setMode("artist")}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-2.5 rounded-lg transition-colors ${
                mode === "artist" ? "bg-brand-red/20 text-brand-red" : "text-white/50 hover:text-white"
              }`}
              data-testid="login-mode-artist"
            >
              <Mic2 size={13} /> Artiste
            </button>
          </div>

          <h1 className="font-display text-3xl font-black tracking-tight mb-2">
            {mode === "artist" ? "Connexion artiste" : "Connexion visiteur"}
          </h1>
          <p className="text-white/60 mb-7">
            {mode === "artist"
              ? "Accède à ton studio pour publier et suivre ta fanbase."
              : "Retrouve tes playlists, likes et artistes suivis."}
          </p>

          {googleClientId && (
            <>
              <div ref={googleBtnRef} className="flex justify-center mb-5" data-testid="login-google-btn" />
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-white/40 uppercase tracking-widest">ou</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-4">
            <InputField icon={<Mail size={16} />} type="email" placeholder="Email" value={email} onChange={setEmail} testid="login-email" />
            <InputField icon={<Lock size={16} />} type="password" placeholder="Mot de passe" value={password} onChange={setPassword} testid="login-password" />
            <div className="flex justify-end -mt-2">
              <Link to={`/forgot-password${redirectTo !== "/" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`} className="text-xs text-white/60 hover:text-yellow-300 font-bold uppercase tracking-widest transition" data-testid="forgot-pw-link">
                Mot de passe oublié ?
              </Link>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="login-submit-btn">
              <LogIn size={16} /> {loading
                ? "Connexion..."
                : mode === "artist" ? "Accéder à mon studio" : "Accéder à mon espace"}
            </button>
          </form>

          <p className="text-sm text-white/60 text-center mt-6">
            Pas encore de compte ?{" "}
            <Link
              to={`/register?as=${mode}`}
              className="text-brand-red hover:text-brand-redHover font-semibold"
              data-testid="goto-register"
            >
              {mode === "artist" ? "Créer un compte artiste" : "Créer un compte visiteur"}
            </Link>
          </p>
        </div>

        {/* Propulsé par Top Mada — uniquement sur voamada.com, pas sur arnakclan.com */}
        {!isArnakDomain && (
          <div className="mt-8 flex justify-center" data-testid="login-poweredby-topmada">
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="group inline-flex items-center gap-3 px-5 py-3 rounded-full bg-white/[0.02] border border-white/5 hover:border-amber-400/30 transition"
            >
              <img
                src="/assets/topmada-logo.png"
                alt="Top Mada"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-amber-400/30 group-hover:ring-amber-400/60 transition"
                loading="lazy"
              />
              <span className="flex flex-col leading-tight">
                <span className="text-[9px] uppercase tracking-[0.3em] text-white/35 group-hover:text-white/55">Propulsé par</span>
                <span className="text-sm font-black tracking-tight bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                  Top Mada
                </span>
              </span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const InputField = ({ icon, type, placeholder, value, onChange, testid }) => (
  <div className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 focus-within:border-brand-red/50">
    <span className="text-white/40">{icon}</span>
    <input
      required
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex-1 bg-transparent outline-none text-sm"
      data-testid={`${testid}-input`}
    />
  </div>
);

export default LoginPage;
